/**
 * Gestionnaire pour le glossaire
 * Gère l'initialisation et la configuration des règles de glossaire depuis l'API
 * Applique les transformations de termes selon les langues configurées
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { fetchConfigApi } from '../utils/config-calls.js';
import { createSpecializedHelper } from './regexpHelper.js';
import { getAiWorkingLanguage } from '../utils/routing-config-manager.js';

// Helper spécialisé pour le glossaire
let glossaryHelper = null;

/**
 * Échappe les caractères spéciaux pour créer une regex qui recherche un mot complet
 * @param {string} text - Texte à échapper
 * @returns {string} - Pattern regex pour mot complet
 */
const escapeRegexAndMakeWordBoundary = (text) => {
  // Échapper les caractères spéciaux de regex
  const escaped = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Créer un pattern qui recherche le mot complet (avec limites de mots)
  return `\\b${escaped}\\b`;
};

/**
 * Fonction de chargement des règles de glossaire
 * @param {Object} helper - Instance du RegexpHelper
 * @param {string} languageFrom - Langue source (peut être vide)
 * @param {string} languageTo - Langue cible
 */
const loadGlossaryRules = async (helper, languageFrom = '', languageTo = '') => {
  try {
    debug.log(config, 'GlossaryHelper: Chargement des règles de glossaire depuis l\'API');
    
    // Réinitialiser les règles
    helper.clearRules();
    
    // languageTo est maintenant requis (passé depuis runTemplate)
    if (!languageTo) {
      languageTo = 'FR';  // Fallback sécurisé uniquement si non fourni
    }
    
    // Construire les paramètres de l'API
    let apiUrl = '/api/config/read/glossary';
    const params = new URLSearchParams();
    
    if (languageFrom) {
      params.append('language_from', languageFrom);
    }
    params.append('language_to', languageTo);
    
    if (params.toString()) {
      apiUrl += '?' + params.toString();
    }
    
    debug.log(config, `GlossaryHelper: Appel API glossaire: ${apiUrl}`);
    
    // Appel à l'API pour récupérer les règles de glossaire
    const response = await fetchConfigApi(apiUrl);
    
    if (!response || !response.success || !response.glossary) {
      throw new Error('Impossible de récupérer les règles de glossaire depuis l\'API');
    }
    
    const glossaryEntries = response.glossary;
    debug.log(config, 'GlossaryHelper: Entrées de glossaire récupérées:', glossaryEntries.length);
    
    // Transformer les entrées de glossaire en règles regex
    const rules = [];
    
    for (const entry of glossaryEntries) {
      if (!entry.original_text || !entry.translated_text) {
        debug.warn(config, 'GlossaryHelper: Entrée de glossaire incomplète ignorée:', entry);
        continue;
      }
      
      let searchPattern;
      
      if (entry.is_regex) {
        // Si c'est déjà une regex, utiliser directement
        searchPattern = entry.original_text;
      } else {
        // Sinon, créer un pattern pour mot complet
        searchPattern = escapeRegexAndMakeWordBoundary(entry.original_text);
      }
      
      rules.push({
        search_pattern: searchPattern,
        replace_pattern: entry.translated_text,
        metadata: {
          originalText: entry.original_text,
          translatedText: entry.translated_text,
          isRegex: entry.is_regex,
          languageFrom: languageFrom,
          languageTo: languageTo
        }
      });
    }
    
    // Charger les règles dans le helper
    helper.loadRulesFromArray(rules, 'API:glossary', { 
      languageFrom: languageFrom,
      languageTo: languageTo,
      source: response.source 
    });
    
    debug.log(config, `GlossaryHelper: Règles de glossaire chargées: ${helper.getRulesCount()} règles totales`);
    
  } catch (error) {
    debug.error(config, 'GlossaryHelper: Erreur lors du chargement des règles de glossaire:', error);
    throw error;
  }
};

/**
 * Crée une fonction de chargement de glossaire avec des paramètres spécifiques
 * @param {string} languageFrom - Langue source
 * @param {string} languageTo - Langue cible
 * @returns {Function} - Fonction de chargement pour le helper
 */
const createGlossaryLoader = (languageFrom, languageTo) => {
  return async (helper) => {
    await loadGlossaryRules(helper, languageFrom, languageTo);
  };
};

/**
 * Initialise le helper de glossaire avec des langues spécifiques
 * @param {string} languageFrom - Langue source (optionnel)
 * @param {string} languageTo - Langue cible (optionnel, défaut: 'FR')
 */
export const initializeGlossaryHelper = async (languageFrom = '', languageTo = '') => {
  // Fallback sécurisé si languageTo n'est pas fourni
  if (!languageTo) {
    languageTo = 'FR';
  }
  
  debug.log(config, `GlossaryHelper: Initialisation avec languageFrom='${languageFrom}', languageTo='${languageTo}'`);
  
  // Créer un nouveau helper avec les paramètres spécifiques
  const loaderFunction = createGlossaryLoader(languageFrom, languageTo);
  glossaryHelper = createSpecializedHelper('Glossary', loaderFunction);
  
  await glossaryHelper.initialize();
};

/**
 * Vérifie si le glossaire est initialisé
 * @returns {boolean}
 */
export const isGlossaryHelperInitialized = () => {
  return glossaryHelper ? glossaryHelper.getInitialized() : false;
};

/**
 * Applique les règles de glossaire sur un texte
 * @param {string} text - Texte à traiter
 * @param {string} languageFrom - Langue source (optionnel)
 * @param {string} languageTo - Langue cible (optionnel)
 * @returns {Promise<string>} - Texte traité
 */
export const applyGlossary = async (text, languageFrom = '', languageTo = '') => {
  if (!text) return '';
  
  // Vérifier si le helper est initialisé avec les bonnes langues
  const currentLanguageTo = languageTo || await getAiWorkingLanguage() || 'FR';
  
  if (!glossaryHelper || !glossaryHelper.getInitialized()) {
    debug.log(config, 'GlossaryHelper: Initialisation automatique du glossaire');
    await initializeGlossaryHelper(languageFrom, currentLanguageTo);
  }
  
  if (!glossaryHelper || !glossaryHelper.getInitialized()) {
    debug.warn(config, 'GlossaryHelper: Helper de glossaire non initialisé');
    return text;
  }

  return glossaryHelper.applyConversionRules(text, 'Glossaire');
};

/**
 * Recharge les règles de glossaire depuis l'API
 * @param {string} languageFrom - Langue source (optionnel)
 * @param {string} languageTo - Langue cible (optionnel)
 */
export const reloadGlossaryRules = async (languageFrom = '', languageTo = '') => {
  debug.log(config, 'GlossaryHelper: Rechargement des règles de glossaire');
  
  if (glossaryHelper) {
    // Réinitialiser avec les nouvelles langues
    const currentLanguageTo = languageTo || await getAiWorkingLanguage() || 'FR';
    const loaderFunction = createGlossaryLoader(languageFrom, currentLanguageTo);
    
    // Créer un nouveau helper avec les nouveaux paramètres
    glossaryHelper = createSpecializedHelper('Glossary', loaderFunction);
    await glossaryHelper.initialize();
  } else {
    await initializeGlossaryHelper(languageFrom, languageTo);
  }
};

/**
 * Retourne les règles de glossaire actuelles
 * @returns {Object} - Règles de glossaire
 */
export const getGlossaryRules = () => {
  if (!glossaryHelper) {
    return {};
  }
  return glossaryHelper.getRules();
};

/**
 * Retourne le nombre de règles de glossaire
 * @returns {number} - Nombre de règles
 */
export const getGlossaryRulesCount = () => {
  if (!glossaryHelper) {
    return 0;
  }
  return glossaryHelper.getRulesCount();
};

/**
 * Nettoie le helper de glossaire
 */
export const clearGlossaryHelper = () => {
  if (glossaryHelper) {
    glossaryHelper.clearRules();
    glossaryHelper = null;
  }
  debug.log(config, 'GlossaryHelper: Helper nettoyé');
};
